import * as yup from 'yup';

// Invoice item validation schema
export const invoiceItemSchema = yup.object().shape({
  service_id: yup.number().nullable(),
  name: yup.string().required('Το όνομα είναι υποχρεωτικό').max(150, 'Μέγιστο 150 χαρακτήρες'),
  description: yup.string().nullable(),
  quantity: yup.number().min(1, 'Η ποσότητα πρέπει να είναι τουλάχιστον 1').required('Η ποσότητα είναι υποχρεωτική'),
  unit_price: yup.number().min(0, 'Η τιμή πρέπει να είναι θετική').required('Η τιμή είναι υποχρεωτική'),
  vat_rate: yup.number().min(0, 'Το ΦΠΑ πρέπει να είναι θετικό').max(100, 'Το ΦΠΑ δεν μπορεί να είναι πάνω από 100%').default(24),
  sort_order: yup.number().default(0)
});

// Invoice creation schema
export const invoiceCreateSchema = yup.object().shape({
  client_id: yup.number().required('Ο πελάτης είναι υποχρεωτικός'),
  quote_id: yup.number().nullable(),
  invoice_number: yup.string().required('Ο αριθμός τιμολογίου είναι υποχρεωτικός').max(50, 'Μέγιστο 50 χαρακτήρες'),
  invoice_type: yup.string().oneOf(['invoice', 'receipt', 'credit_note', 'proforma_invoice'], 'Μη έγκυρος τύπος τιμολογίου').default('invoice'),
  reference_number: yup.string().nullable().max(50, 'Μέγιστο 50 χαρακτήρες'),
  title: yup.string().nullable().max(150, 'Μέγιστο 150 χαρακτήρες'),
  issue_date: yup.date().required('Η ημερομηνία έκδοσης είναι υποχρεωτική'),
  due_date: yup.date().nullable().test('due-date', 'Η ημερομηνία λήξης πρέπει να είναι μετά την έκδοση', function(value) {
    const { issue_date } = this.parent;
    if (!value || !issue_date) return true;
    return new Date(value) >= new Date(issue_date);
  }),
  payment_method: yup.string().oneOf(['cash', 'bank_transfer', 'card', 'check', 'other'], 'Μη έγκυρη μέθοδος πληρωμής').nullable(),
  notes: yup.string().nullable(),
  invoice_items: yup.array().of(invoiceItemSchema).default([])
});

// Invoice update schema
export const invoiceUpdateSchema = yup.object().shape({
  invoice_type: yup.string().oneOf(['invoice', 'receipt', 'credit_note', 'proforma_invoice'], 'Μη έγκυρος τύπος τιμολογίου').nullable(),
  reference_number: yup.string().nullable().max(50, 'Μέγιστο 50 χαρακτήρες'),
  title: yup.string().nullable().max(150, 'Μέγιστο 150 χαρακτήρες'),
  issue_date: yup.date().nullable(),
  due_date: yup.date().nullable().test('due-date', 'Η ημερομηνία λήξης πρέπει να είναι μετά την έκδοση', function(value) {
    const { issue_date } = this.parent;
    if (!value || !issue_date) return true;
    return new Date(value) >= new Date(issue_date);
  }),
  payment_status: yup.string().oneOf(['unpaid', 'partial', 'paid', 'overdue'], 'Μη έγκυρο status πληρωμής').nullable(),
  payment_method: yup.string().oneOf(['cash', 'bank_transfer', 'card', 'check', 'other'], 'Μη έγκυρη μέθοδος πληρωμής').nullable(),
  notes: yup.string().nullable(),
  invoice_items: yup.array().of(invoiceItemSchema).nullable()
});

// Payment update schema
export const invoicePaymentSchema = yup.object().shape({
  paid_amount: yup.number().min(0, 'Το ποσό πληρωμής πρέπει να είναι θετικό').required('Το ποσό πληρωμής είναι υποχρεωτικό'),
  payment_method: yup.string().oneOf(['cash', 'bank_transfer', 'card', 'check', 'other'], 'Μη έγκυρη μέθοδος πληρωμής').nullable(),
  notes: yup.string().nullable()
});

// Service selection schema for invoice creation
export const serviceSelectionSchema = yup.object().shape({
  client_id: yup.number().required('Ο πελάτης είναι υποχρεωτικός'),
  service_ids: yup.array().of(yup.number()).min(1, 'Πρέπει να επιλέξετε τουλάχιστον μία υπηρεσία').required('Οι υπηρεσίες είναι υποχρεωτικές'),
  invoice_number: yup.string().required('Ο αριθμός τιμολογίου είναι υποχρεωτικός').max(50, 'Μέγιστο 50 χαρακτήρες'),
  title: yup.string().nullable().max(150, 'Μέγιστο 150 χαρακτήρες'),
  issue_date: yup.date().required('Η ημερομηνία έκδοσης είναι υποχρεωτική'),
  due_date: yup.date().nullable().test('due-date', 'Η ημερομηνία λήξης πρέπει να είναι μετά την έκδοση', function(value) {
    const { issue_date } = this.parent;
    if (!value || !issue_date) return true;
    return new Date(value) >= new Date(issue_date);
  }),
  notes: yup.string().nullable()
});

// Greek calculations schema
export const greekCalculationsSchema = yup.object().shape({
  cost: yup.number().min(0, 'Το κόστος πρέπει να είναι θετικό').required('Το κόστος είναι υποχρεωτικό'),
  vat_percentage: yup.number().min(0, 'Το ΦΠΑ πρέπει να είναι θετικό').max(100, 'Το ΦΠΑ δεν μπορεί να είναι πάνω από 100%').default(24),
  invoice_type: yup.string().nullable(),
  service_type: yup.string().nullable(),
  service_name: yup.string().nullable()
});

// MyDATA configuration schema
export const mydataConfigSchema = yup.object().shape({
  company_vat: yup.string().required('Ο ΑΦΜ εταιρείας είναι υποχρεωτικός').matches(/^\d{9}$/, 'Ο ΑΦΜ πρέπει να έχει 9 ψηφία'),
  user_id: yup.string().required('Το MyDATA user ID είναι υποχρεωτικό'),
  subscription_key: yup.string().required('Το MyDATA subscription key είναι υποχρεωτικό')
});

// Invoice filters schema
export const invoiceFiltersSchema = yup.object().shape({
  client_id: yup.number().nullable(),
  invoice_type: yup.string().oneOf(['invoice', 'receipt', 'credit_note', 'proforma_invoice']).nullable(),
  payment_status: yup.string().oneOf(['unpaid', 'partial', 'paid', 'overdue']).nullable(),
  date_from: yup.date().nullable(),
  date_to: yup.date().nullable(),
  search: yup.string().nullable(),
  sort_by: yup.string().oneOf(['invoice_number', 'issue_date', 'due_date', 'total_amount', 'payment_status', 'created_at']).default('created_at'),
  sort_order: yup.string().oneOf(['asc', 'desc']).default('desc')
});

// Default values for forms
export const defaultInvoiceValues = {
  invoice_type: 'invoice',
  issue_date: new Date().toISOString().split('T')[0],
  due_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 14 days from now
  payment_method: 'card',
  invoice_items: []
};

export const defaultServiceSelectionValues = {
  service_ids: [],
  issue_date: new Date().toISOString().split('T')[0],
  due_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
};

export const defaultGreekCalculationsValues = {
  vat_percentage: 24,
  cost: 0
};

// Validation helpers
export const validateInvoiceNumber = (value) => {
  if (!value) return 'Ο αριθμός τιμολογίου είναι υποχρεωτικός';
  if (value.length > 50) return 'Μέγιστο 50 χαρακτήρες';
  return null;
};

export const validateVATRate = (value) => {
  if (value < 0) return 'Το ΦΠΑ πρέπει να είναι θετικό';
  if (value > 100) return 'Το ΦΠΑ δεν μπορεί να είναι πάνω από 100%';
  return null;
};

export const validateDueDate = (dueDate, issueDate) => {
  if (!dueDate || !issueDate) return null;
  if (new Date(dueDate) < new Date(issueDate)) {
    return 'Η ημερομηνία λήξης πρέπει να είναι μετά την έκδοση';
  }
  return null;
};

// Greek tax calculation helpers
export const calculateVAT = (amount, vatRate = 24) => {
  return (amount * vatRate) / 100;
};

export const calculateWithholding = (amount, withholdingRate = 20) => {
  return (amount * withholdingRate) / 100;
};

export const calculateTotal = (netAmount, vatAmount, withholdingAmount = 0) => {
  return netAmount + vatAmount - withholdingAmount;
};

export const shouldApplyWithholding = (serviceType, serviceName, amount) => {
  return serviceType === 'subscription' && 
         serviceName.toLowerCase() === 'support' && 
         amount >= 300;
};
